
import { useState, useEffect } from "react";
import { usePaymentProcessor } from "@/hooks/usePaymentProcessor";
import { useSubscriptionInitializer } from "@/hooks/useSubscriptionInitializer";
import { LoadingStates } from "@/components/paywall/LoadingStates";
import { PaywallHeader } from "@/components/paywall/PaywallHeader";
import { PricingCards } from "@/components/paywall/PricingCards";
import { InvitationCodeSection } from "@/components/paywall/InvitationCodeSection";
import { PaymentDialog } from "@/components/paywall/PaymentDialog";
import { supabase } from "@/integrations/supabase/client";
import { scrollToTop } from "@/utils/scrollUtils";

const Paywall = () => {
  // State
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [invitationCode, setInvitationCode] = useState('');
  const [redeemingCode, setRedeemingCode] = useState(false);

  // Custom hooks
  const { stripePromise, loading } = useSubscriptionInitializer();
  const { 
    processingPayment, 
    redirecting, 
    handlePaymentSuccess, 
    handlePaymentError, 
    handleRedeemCode 
  } = usePaymentProcessor();

  // Map of price IDs to plan types for reference
  const priceIdToPlanMap = {
    "price_1QvOaiG3RRUf49cQgmkP8MtT": "monthly",
    "price_1QvObYG3RRUf49cQp6XPRkNN": "yearly",
    "price_1QvOcGG3RRUf49cQlU0O22em": "lifetime"
  };

  // Scroll to top when component mounts
  useEffect(() => {
    scrollToTop();
  }, []);

  // Debugging: Log authenticated user on component mount
  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      console.log('Current auth session:', data.session ? `User: ${data.session.user.id}` : 'Not authenticated');
    };
    
    checkAuth();
  }, []);

  // Event handlers
  const handleSubscribe = (priceId: string) => {
    console.log(`Subscribe clicked for price ID: ${priceId}`);
    setSelectedPlan(priceId);
    setShowPaymentDialog(true);
  };

  const onPaymentSuccess = (paymentMethodId: string) => {
    console.log(`Payment successful with method ID: ${paymentMethodId}`);
    setShowPaymentDialog(false);
    handlePaymentSuccess(paymentMethodId, selectedPlan);
  };

  const onRedeemCode = () => {
    console.log(`Attempting to redeem code: ${invitationCode}`);
    handleRedeemCode(invitationCode, setRedeemingCode);
  };

  // Show loading or redirecting states if applicable
  if (loading || redirecting) {
    console.log(`Showing state - loading: ${loading}, redirecting: ${redirecting}`);
    return <LoadingStates loading={loading} redirecting={redirecting} />;
  }

  console.log('Rendering Paywall UI');
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        <PaywallHeader />

        <PricingCards 
          processingPayment={processingPayment}
          handleSubscribe={handleSubscribe}
        />

        <InvitationCodeSection 
          invitationCode={invitationCode}
          setInvitationCode={setInvitationCode}
          handleRedeemCode={onRedeemCode}
          redeemingCode={redeemingCode}
        />

        <PaymentDialog
          showPaymentDialog={showPaymentDialog}
          setShowPaymentDialog={setShowPaymentDialog}
          selectedPlan={selectedPlan}
          stripePromise={stripePromise}
          onSuccess={onPaymentSuccess}
          onError={handlePaymentError}
          loading={processingPayment}
        />
      </div>
    </div>
  );
};

export default Paywall;
